import { z } from 'zod';

/**
 * Per-package dismantling DTOs — mirrors the backend contract.
 * A dismantling_session batches N inbound_packages (N >= 1) into one set of
 * outputs; the join set is carried by `inbound_package_ids`.
 */

export const dismantlingSessionStatusValues = ['imported', 'confirmed', 'locked'] as const;
export type DismantlingSessionStatus = (typeof dismantlingSessionStatusValues)[number];

export const dismantlingOperationalStatusValues = [
  'draft',
  'in_progress',
  'needs_evidence',
  'ready_to_confirm',
  'confirmed',
  'locked',
] as const;
export type DismantlingOperationalStatus = (typeof dismantlingOperationalStatusValues)[number];

/**
 * Legacy database values retained for read compatibility only.
 * New work-entry mutations do not accept or send output_state.
 */
export const dismantlingOutputStateValues = [
  'materialized',
  'inventory',
  'shipped',
  'secondary_process',
] as const;
export type DismantlingOutputState = (typeof dismantlingOutputStateValues)[number];

export const dismantlingOutputKindValues = ['final_material', 'intermediate'] as const;
export type DismantlingOutputKind = (typeof dismantlingOutputKindValues)[number];

/**
 * A method is whatever the processor calls it — the codes come from
 * `dismantling_method_masters`, not from this file. The two the platform used
 * to hardcode are kept here only so legacy sessions can still be labelled.
 */
export type DismantlingMethod = string;
export const legacyDismantlingMethodValues = ['manual_light', 'manual_heavy'] as const;

/** Dismantling route_types that surface a package on the 拆解 page. */
export const dismantlingRouteValues = [
  'manual_dismantling',
  // Written before the split was retired; still routed to dismantling.
  'manual_light_dismantling',
  'manual_heavy_dismantling',
] as const;
export type DismantlingRoute = (typeof dismantlingRouteValues)[number];

// ---------------------------------------------------------------------------
// GET /api/monthly-batches/:batch_id/dismantling-packages
// ---------------------------------------------------------------------------

export const dismantlingPackageSchema = z.object({
  inbound_package_id: z.string(),
  package_code: z.string(),
  raw_incoming_item_name: z.string(),
  incoming_item_name: z.string(),
  category_group: z.string().nullable().optional(),
  warehouse_id: z.string().nullable().optional(),
  warehouse_code: z.string().nullable().optional(),
  warehouse_display_name: z.string().nullable().optional(),
  gross_kg: z.string().nullable().optional(),
  tare_kg: z.string().nullable().optional(),
  net_kg: z.string().nullable().optional(),
  route_type: z.string(),
  lot_code: z.string(),
  inbound_date: z.string(),
  source_company_id: z.string(),
  dismantling_session_id: z.string().nullable().optional(),
  session_status: z.string().nullable().optional(),
  operational_status: z.enum(dismantlingOperationalStatusValues).nullable().optional(),
  photo_count: z.number(),
  output_count: z.number(),
  progress_percent: z.number().min(0).nullable().default(null),
  input_weight_kg: z.string().nullable().optional(),
  output_weight_kg: z.string().nullable().optional(),
});

export type DismantlingPackageDto = z.infer<typeof dismantlingPackageSchema>;

// ---------------------------------------------------------------------------
// Session detail
// ---------------------------------------------------------------------------

export const dismantlingOutputSchema = z.object({
  dismantling_output_id: z.string(),
  dismantling_session_id: z.string(),
  /** Daily work entry this output was booked under; null for legacy rows. */
  work_entry_id: z.string().nullable().optional(),
  inbound_lot_id: z.string().nullable().optional(),
  material_id: z.string(),
  raw_material_name: z.string(),
  kg: z.string(),
  output_kind: z.literal('final_material').optional().default('final_material'),
  output_state: z.enum(dismantlingOutputStateValues).nullable().optional(),
  warehouse_id: z.string().nullable().optional(),
  warehouse_code: z.string().nullable().optional(),
  warehouse_display_name: z.string().nullable().optional(),
  // Read-only legacy field. New mutations must use warehouse_id.
  storage_location_id: z.string().nullable().optional(),
  photo_evidence_file_ids: z.array(z.string()).default([]),
});

export type DismantlingOutputDto = z.infer<typeof dismantlingOutputSchema>;

export const dismantlingIntermediateStatusValues = ['draft', 'confirmed', 'voided'] as const;
export type DismantlingIntermediateStatus = (typeof dismantlingIntermediateStatusValues)[number];

export const dismantlingIntermediateSchema = z.object({
  dismantling_intermediate_id: z.string(),
  dismantling_work_entry_id: z.string(),
  incoming_item_id: z.string(),
  incoming_item_name: z.string().nullable().optional(),
  weight_kg: z.string(),
  source_inbound_package_ids: z.array(z.string()).default([]),
  warehouse_id: z.string().nullable().optional(),
  warehouse_code: z.string().nullable().optional(),
  warehouse_display_name: z.string().nullable().optional(),
  next_route_type: z.enum(dismantlingRouteValues),
  photo_evidence_file_ids: z.array(z.string()).default([]),
  reason: z.string().nullable().optional(),
  status: z.enum(dismantlingIntermediateStatusValues),
  new_inbound_package_id: z.string().nullable().optional(),
});

export type DismantlingIntermediateDto = z.infer<typeof dismantlingIntermediateSchema>;

export const dismantlingWorkEntrySchema = z.object({
  dismantling_work_entry_id: z.string(),
  work_date: z.string(),
  worker_count: z.number(),
  progress_increment_percent: z.number().min(0).nullable(),
  cumulative_progress_percent: z.number().min(0).nullable().optional(),
  output_weight_kg: z.string().nullable().optional(),
  cumulative_output_weight_kg: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  is_legacy_backfill: z.boolean().default(false),
  created_at: z.string(),
  voided_at: z.string().nullable().optional(),
  void_reason: z.string().nullable().optional(),
  outputs: z.array(dismantlingOutputSchema).default([]),
  intermediate_items: z.array(dismantlingIntermediateSchema).default([]),
});

export type DismantlingWorkEntryDto = z.infer<typeof dismantlingWorkEntrySchema>;

/**
 * One input pallet attached to a session. Column set mirrors the backend
 * `SessionPackageDto` (package_seq, item display name, net_kg).
 */
export const sessionPackageSchema = z.object({
  inbound_package_id: z.string(),
  package_seq: z.number(),
  package_code: z.string(),
  incoming_item_name: z.string(),
  category_group: z.string().nullable().optional(),
  net_kg: z.string().nullable().optional(),
});
export type SessionPackageDto = z.infer<typeof sessionPackageSchema>;

export const sessionRevisionSchema = z.object({
  revision_id: z.string(),
  changed_at: z.string(),
  changed_by: z.string(),
  changed_by_name: z.string(),
  change_reason: z.string(),
  change_summary: z.unknown(),
});
export type SessionRevisionDto = z.infer<typeof sessionRevisionSchema>;

export const dismantlingSessionDetailSchema = z.object({
  dismantling_session_id: z.string(),
  monthly_batch_id: z.string(),
  packages: z.array(sessionPackageSchema).default([]),
  sorting_decision_id: z.string().nullable().optional(),
  dismantling_date: z.string(),
  worker_count: z.number(),
  dismantling_method: z.string(),
  supervisor_id: z.string().nullable().optional(),
  status: z.enum(dismantlingSessionStatusValues),
  operational_status: z.enum(dismantlingOperationalStatusValues).optional(),
  notes: z.string().nullable().optional(),
  progress_percent: z.number().min(0).nullable().default(null),
  input_weight_kg: z.string().nullable().optional(),
  output_weight_kg: z.string().nullable().optional(),
  work_entries: z.array(dismantlingWorkEntrySchema).default([]),
  outputs: z.array(dismantlingOutputSchema).default([]),
  intermediates: z.array(dismantlingIntermediateSchema).default([]),
  revisions: z.array(sessionRevisionSchema).default([]),
});

export type DismantlingSessionDetailDto = z.infer<typeof dismantlingSessionDetailSchema>;

// ---------------------------------------------------------------------------
// Create / Update bodies (form inputs)
// ---------------------------------------------------------------------------

export const positiveKgSchema = z
  .string()
  .min(1, 'kgを入力してください')
  .regex(/^(?:0|[1-9]\d*)(?:\.\d{1,3})?$/, '0以上の数値（小数点3桁まで）を入力してください');
export const strictlyPositiveKgSchema = positiveKgSchema.refine(
  (value) => Number.parseFloat(value) > 0,
  '0より大きい重量を入力してください',
);

export const dismantlingOutputInputSchema = z
  .object({
    output_kind: z.literal('final_material'),
    material_id: z.string().min(1, '素材を選択してください'),
    raw_material_name: z.string().min(1).max(200),
    kg: positiveKgSchema,
    warehouse_id: z.string().min(1, '倉庫を選択してください'),
    photo_evidence_file_ids: z.array(z.string().min(1)).default([]),
  })
  .strict();
export type DismantlingOutputInput = z.infer<typeof dismantlingOutputInputSchema>;

export const dismantlingIntermediateInputSchema = z
  .object({
    incoming_item_id: z.string().min(1, '品目を選択してください'),
    weight_kg: strictlyPositiveKgSchema,
    warehouse_id: z.string().min(1, '倉庫を選択してください'),
    next_route_type: z.enum(dismantlingRouteValues),
    photo_evidence_file_ids: z.array(z.string().min(1)).default([]),
  })
  .strict();
export type DismantlingIntermediateInput = z.infer<typeof dismantlingIntermediateInputSchema>;

export const addDismantlingWorkEntrySchema = z
  .object({
    work_date: z.string().min(1, '作業日を入力してください'),
    worker_count: z.number().int().min(1).max(1000),
    outputs: z.array(dismantlingOutputInputSchema).default([]),
    intermediate_items: z.array(dismantlingIntermediateInputSchema).default([]),
    notes: z.string().max(2000).optional(),
  })
  .strict();
export type AddDismantlingWorkEntryInput = z.infer<typeof addDismantlingWorkEntrySchema>;

export const createDismantlingSessionSchema = z.object({
  inbound_package_ids: z.array(z.string().min(1)).min(1),
  dismantling_date: z.string().min(1, '作業日を入力してください'),
  worker_count: z.number().int().min(0).max(1000),
  supervisor_id: z.string().nullable().optional(),
  // Whatever the processor calls it; the server checks it against
  // that company's own list.
  dismantling_method: z.string().min(1).max(64),
  outputs: z.array(dismantlingOutputInputSchema).default([]),
  notes: z.string().max(2000).optional(),
  change_reason: z.string().max(2000).optional(),
  // The first day's work, saved in the same transaction: a refused entry
  // leaves no empty session behind.
  first_work_entry: addDismantlingWorkEntrySchema.optional(),
});
export type CreateDismantlingSessionInput = z.infer<typeof createDismantlingSessionSchema>;

export const updateDismantlingSessionSchema = z.object({
  inbound_package_ids: z.array(z.string().min(1)).min(1).optional(),
  dismantling_date: z.string().optional(),
  worker_count: z.number().int().min(0).max(1000).optional(),
  supervisor_id: z.string().nullable().optional(),
  dismantling_method: z.string().min(1).max(64).optional(),
  outputs: z.array(dismantlingOutputInputSchema).optional(),
  notes: z.string().max(2000).optional(),
  change_reason: z.string().min(1, '修改原因が必要です').max(2000),
});
export type UpdateDismantlingSessionInput = z.infer<typeof updateDismantlingSessionSchema>;

export interface DismantlingCancelBlocker {
  code: string;
  count: number;
}

export interface DismantlingCancelPreview {
  dismantling_session_id: string;
  status?: string | null;
  purge_after: string;
  can_cancel: boolean;
  blocker_count: number;
  blockers: DismantlingCancelBlocker[];
  input_package_count?: number;
  work_entry_count?: number;
  /** Work entries that are not voided; voided 0 kg rows are not attendance. */
  active_work_entry_count?: number;
  dismantling_output_count?: number;
  final_output_count?: number;
  intermediate_count?: number;
  derived_inbound_package_count?: number;
  derived_package_count?: number;
  inventory_movement_count?: number;
  evidence_file_count?: number;
  impact_counts?: Record<string, number>;
}

export interface DismantlingCancelResult {
  dismantling_session_id: string;
  archive_id: string;
  cancelled_at: string;
  purge_after: string;
  impact_counts?: Record<string, number>;
}

export interface CancelledDismantlingSession {
  archive_id: string;
  dismantling_session_id: string;
  monthly_batch_id: string;
  package_codes: string[];
  dismantling_date?: string | null;
  source_kind: string;
  return_step: string;
  next_state: string;
  archived_status?: DismantlingSessionStatus | string | null;
  cancelled_at: string;
  cancelled_by: string;
  cancelled_by_display_name: string;
  cancelled_by_email: string;
  cancel_reason: string;
  purge_after: string;
  recoverable: boolean;
  days_remaining: number;
  purged_at?: string | null;
  restored_at?: string | null;
  impact_counts?: Record<string, number>;
}

export const getDismantlingOperationalStatus = (
  session: Pick<
    DismantlingSessionDetailDto,
    | 'status'
    | 'operational_status'
    | 'progress_percent'
    | 'outputs'
    | 'intermediates'
    | 'work_entries'
  >,
): DismantlingOperationalStatus => {
  if (session.operational_status) return session.operational_status;
  if (session.status === 'locked') return 'locked';
  if (session.status === 'confirmed') return 'confirmed';

  const intermediates =
    (session.intermediates ?? []).length > 0
      ? (session.intermediates ?? [])
      : session.work_entries.flatMap((entry) => entry.intermediate_items ?? []);
  const hasOutput =
    session.outputs.length > 0 || intermediates.some((item) => item.status !== 'voided');
  if (!hasOutput) {
    const legacyProgress = session.progress_percent ?? 0;
    const activeEntryCount = session.work_entries.filter((entry) => !entry.voided_at).length;
    if (legacyProgress >= 100 && activeEntryCount > 0) return 'needs_evidence';
    return activeEntryCount > 0 ? 'in_progress' : 'draft';
  }
  const finalOutputsReady = session.outputs.every((output) => !!output.warehouse_id);
  const intermediatesReady = intermediates.every(
    (item) =>
      item.status === 'voided' ||
      (item.status === 'confirmed' && !!item.warehouse_id && !!item.new_inbound_package_id),
  );

  return hasOutput && finalOutputsReady && intermediatesReady
    ? 'ready_to_confirm'
    : 'needs_evidence';
};

const parseKg = (value: string | null | undefined): number | null => {
  if (value == null) return null;
  const kg = Number.parseFloat(value);
  return Number.isFinite(kg) ? kg : null;
};

/** No output weight recorded yet (no final material, no active intermediate). */
export const packageHasNoRecordedOutput = (
  pkg: Pick<DismantlingPackageDto, 'output_weight_kg' | 'output_count' | 'progress_percent'>,
): boolean => {
  const kg = parseKg(pkg.output_weight_kg);
  if (kg != null) return kg === 0;
  return pkg.output_count === 0 && (pkg.progress_percent ?? 0) === 0;
};

/** Same test for a loaded session. */
export const sessionHasNoRecordedOutput = (
  session: Pick<
    DismantlingSessionDetailDto,
    'output_weight_kg' | 'outputs' | 'intermediates' | 'work_entries'
  >,
): boolean => {
  const kg = parseKg(session.output_weight_kg);
  if (kg != null) return kg === 0;
  const intermediates =
    (session.intermediates ?? []).length > 0
      ? (session.intermediates ?? [])
      : session.work_entries.flatMap((entry) => entry.intermediate_items ?? []);
  return session.outputs.length === 0 && intermediates.every((item) => item.status === 'voided');
};

export interface DismantlingConfirmBlockers {
  outputsMissingWarehouse: number;
  intermediatesMissingWarehouse: number;
  draftIntermediates: DismantlingIntermediateDto[];
  /** Only draft intermediates (all with a warehouse) block Complete. */
  onlyDraftsRemain: boolean;
}

/** Why a saved session is not `ready_to_confirm`, one reason per field. */
export const getDismantlingConfirmBlockers = (
  session: Pick<DismantlingSessionDetailDto, 'outputs' | 'intermediates' | 'work_entries'>,
): DismantlingConfirmBlockers => {
  const intermediates =
    (session.intermediates ?? []).length > 0
      ? (session.intermediates ?? [])
      : session.work_entries.flatMap((entry) => entry.intermediate_items ?? []);
  const outputsMissingWarehouse = session.outputs.filter((output) => !output.warehouse_id).length;
  const open = intermediates.filter((item) => item.status !== 'voided');
  const intermediatesMissingWarehouse = open.filter((item) => !item.warehouse_id).length;
  // Only drafts that can actually be confirmed: the backend refuses a draft
  // without a warehouse, which is already reported as missing a warehouse.
  const draftIntermediates = open.filter((item) => item.status === 'draft' && !!item.warehouse_id);
  const confirmedIncomplete = open.filter(
    (item) => item.status === 'confirmed' && (!item.warehouse_id || !item.new_inbound_package_id),
  ).length;
  return {
    outputsMissingWarehouse,
    intermediatesMissingWarehouse,
    draftIntermediates,
    onlyDraftsRemain:
      draftIntermediates.length > 0 &&
      outputsMissingWarehouse === 0 &&
      intermediatesMissingWarehouse === 0 &&
      confirmedIncomplete === 0,
  };
};
